import React, { useState, useEffect, useCallback } from 'react';
import PINDisplay from '../PINDisplay/PINDisplay';
import NumericKeyboard from '../NumericKeyboard/NumericKeyboard';
import { MAX_PIN_LENGTH } from '../../constants/pin';
import {
  Container,
  Title,
  Description,
  ErrorMessage,
} from './PINInput.styles';
import { BLACK } from '@styles/colors';
import { CSSProperties } from 'styled-components/dist/types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router';

// Component props
interface PINInputProps {
  mode: 'validate' | 'create' | 'change';
  onComplete: (result: { 
    success: boolean;
    newPin?: string;
  }) => void;
  enableTitle?: boolean;
  enableDescription?: boolean;
  customTitle?: string;
  customDescription?: string;
  titleFontSize?: number;
  titleFontFamily?: string;
  descriptionFontSize?: number;
  descriptionFontFamily?: string;
  alternativeStyle?: boolean;
  minimalStyle?: boolean;
}

const PINInput: React.FC<PINInputProps> = ({ 
  mode,
  enableTitle = true,
  enableDescription = true,
  customTitle,
  customDescription,
  titleFontSize,
  titleFontFamily,
  descriptionFontSize,
  descriptionFontFamily,
  onComplete,
  alternativeStyle = false,
  minimalStyle = false,
}) => {
  const { t } = useTranslation();
  // Use the auth context
  const { 
    remainingAttempts,
    decrementAttempts,
    resetAttempts,
    verifyPin,
    logout,
  } = useAuth();

  const navigate = useNavigate()

  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [verificationPin, setVerificationPin] = useState('');
  const [verificationMode, setVerificationMode] = useState(false);
  const [newPinMode, setNewPinMode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check if PIN is complete and handle validation
  useEffect(() => {
    if (pin.length === MAX_PIN_LENGTH) {
      handlePinComplete(pin);
    }
  }, [pin]);

  const handleKeyboardPress = useCallback((value: string) => {
    // Only reset error when a new key is pressed
    if (pinError || errorMessage) {
      setPinError(false);
      setErrorMessage('');
    }
    
    if (value === 'delete') {  
        setPin(prev => {
            if (prev.length > 0) {
                return prev.slice(0, -1);
            }
            return prev;
        });
        return;
    }

    if (pin.length >= MAX_PIN_LENGTH) {
        return;
    }

    setPin(prev => prev + value);
  }, [pin, pinError, errorMessage]);

  const handlePinComplete = useCallback(async (value: string) => {
    if (mode === 'validate') {
        // Implement PIN validation
        if (await verifyPin(value)) {
            // PIN is correct - resetAttempts is already called in verifyPin
            onComplete({ success: true });
        } else {
            // PIN is incorrect
            decrementAttempts();
            setPinError(true);
            setPin('');
            
            // Always show error message for incorrect PIN
            if (remainingAttempts <= 1) {
                setErrorMessage(t('authentication.pinInput.error.lastAttempt'));
            } else {
                setErrorMessage(t('authentication.pinInput.error.incorrectPin', { attempts: remainingAttempts }));
            }
            
            // If no attempts left, handle account locking logic
            if (remainingAttempts <= 0) {
                // This would trigger account locking mechanism
                setErrorMessage(t('authentication.pinInput.error.walletLocked'));
                await logout()
                onComplete({ success: false });
                navigate('/onboarding')
            }
        }
    } else if (mode === 'create') {
        // Implement PIN creation
        if (!verificationMode) {
            // First PIN entry - store it and ask for verification
            setVerificationPin(value);
            setVerificationMode(true);
            setPin('');
            setErrorMessage('');
        } else {
            // Verification PIN entry - compare with stored PIN
            if (value === verificationPin) {
                // PINs match - creation successful
                onComplete({ success: true, newPin: value });
            } else {
                // PINs don't match - show error
                setPinError(true);
                setPin('');
                setErrorMessage(t('authentication.pinInput.error.pinMismatch'));
                // Reset to first PIN entry
                setVerificationMode(false);
                setVerificationPin('');
            }
        }
    } else if (mode === 'change') {
        // Implement PIN change with retry functionality and verification
        if (!newPinMode) {
            // Verify the current PIN first
            if (await verifyPin(value)) {
                // Current PIN is correct, move to new PIN entry mode
                setNewPinMode(true);
                // resetAttempts is already called in verifyPin
                setPin('');
                setErrorMessage('');
            } else {
                // Current PIN is incorrect
                decrementAttempts();
                setPinError(true);
                setPin('');
                
                // Show error message for incorrect PIN
                if (remainingAttempts <= 1) {
                    setErrorMessage(t('authentication.pinInput.error.lastAttempt'));
                } else {
                    setErrorMessage(t('authentication.pinInput.error.incorrectPin', { attempts: remainingAttempts }));
                }
                
                // If no attempts left, handle account locking logic
                if (remainingAttempts <= 0) {
                    setErrorMessage(t('authentication.pinInput.error.walletLocked'));
                    await logout()
                    onComplete({ success: false });
                }
            }
        } else if (newPinMode && !verificationMode) {
            // Check if the new PIN is the same as the current PIN
            if (await verifyPin(value)) {
                // New PIN is the same as current PIN - show error
                setPinError(true);
                setPin('');
                setErrorMessage(t('authentication.pinInput.error.samePin'));
            } else {
                // New PIN is different - proceed to verification
                setVerificationPin(value);
                setVerificationMode(true);
                setPin('');
                setErrorMessage('');
            }
        } else {
            // Verification of new PIN
            if (value === verificationPin) {
                // New PINs match - change successful
                onComplete({ success: true, newPin: value });
            } else {
                // PINs don't match - show error
                setPinError(true);
                setPin('');
                setErrorMessage(t('authentication.pinInput.error.pinMismatch'));
                // Reset to new PIN entry (not back to current PIN)
                setVerificationMode(false);
                setVerificationPin('');
            }
        }
    }
  }, [mode, verificationMode, verificationPin, decrementAttempts, resetAttempts, remainingAttempts, onComplete, newPinMode]);
  
  // Determine title and description based on mode and verification state
  const getTitleAndDescription = useCallback(() => {
    if (mode === 'validate') {
      return {
        title: t('authentication.pinInput.validate.title'),
        description: t('authentication.pinInput.validate.description')
      };
    } else if (mode === 'create') {
      return {
        title: verificationMode ? t('authentication.pinInput.create.confirmTitle') : t('authentication.pinInput.create.title'),
        description: verificationMode ? t('authentication.pinInput.create.confirmDescription') : t('authentication.pinInput.create.description')
      };
    } else if (mode === 'change') {
      if (!newPinMode) {
        return {
          title: t('authentication.pinInput.change.currentTitle'),
          description: t('authentication.pinInput.change.currentDescription')
        };
      } else if (newPinMode && !verificationMode) {
        return {
          title: t('authentication.pinInput.change.newTitle'),
          description: t('authentication.pinInput.change.newDescription')
        };
      } else {
        return {
          title: t('authentication.pinInput.change.confirmTitle'),
          description: t('authentication.pinInput.change.confirmDescription')
        };
      }
    }
    
    return { title: 'PIN', description: 'Enter PIN' };
  }, [mode, verificationMode, newPinMode, t]);

  // Use custom title and description if provided, otherwise use the default ones
  const { title: defaultTitle, description: defaultDescription } = getTitleAndDescription();
  const title = customTitle || defaultTitle;
  const description = customDescription || defaultDescription;

  // Determine the style based on the mode
  const contentStyle: CSSProperties = alternativeStyle ? {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingTop: 50,
    gap: 50,
  }
    : 
  {};

  return (
    <Container>
        <div style={contentStyle}>
            {minimalStyle === false ? <div>
              <Title style={{ color: enableTitle ? BLACK : 'transparent', fontSize: titleFontSize, fontFamily: titleFontFamily }}>{title}</Title>
              <Description style={{ color: enableDescription ? BLACK : 'transparent', fontSize: descriptionFontSize, fontFamily: descriptionFontFamily }}>{description}</Description>
            </div> : null}
            
            <PINDisplay 
                value={pin} 
                maxLength={MAX_PIN_LENGTH} 
                error={pinError}
            />
            {(pinError || errorMessage) && <ErrorMessage>{errorMessage || t('authentication.pinInput.error.invalidPin')}</ErrorMessage>}
        </div>
        <NumericKeyboard onChange={handleKeyboardPress} />
    </Container>
  );
};

export default React.memo(PINInput);